import { Callout, Table, type TableColumn } from '@humanforce/mango-ui-react';
import type {
  PayRunPopulationEmployee,
  PayRunPopulationTransaction,
} from './payRunPopulationApiTypes';
import type { PayRunEmployeeStatus } from './payRunEmployeeStatusModel';
import { NotImplementedHint } from './PayRunNotImplemented';

interface PayRunEmployeeRowDetailProps {
  employee: PayRunPopulationEmployee;
  status: PayRunEmployeeStatus;
}

const currency = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' });
const number = new Intl.NumberFormat('en-AU', { maximumFractionDigits: 2 });
const effective = new Intl.DateTimeFormat('en-AU', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'Australia/Sydney',
});

const formatDate = (value: string): string => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : effective.format(parsed);
};

const TONE: Record<PayRunEmployeeStatus['tone'], 'critical' | 'caution' | 'info'> = {
  critical: 'critical',
  caution: 'caution',
  positive: 'info',
};

/**
 * The expanded Employees row: what the collapsed row could not say.
 *
 * Every issue the readiness evaluation raised against this person, in its own words, then the
 * transactions that make up their pay. The design also shows Description, Type and Cost columns
 * and per-line edit controls; the population API supplies none of those, so they are disclosed
 * rather than filled with something invented.
 */
export const PayRunEmployeeRowDetail = ({ employee, status }: PayRunEmployeeRowDetailProps) => {
  const columns: TableColumn[] = [
    {
      field: 'factTypeCode',
      header: 'Code',
      body: (line: PayRunPopulationTransaction) => (
        <span className="mgo-weight-strong">{line.factTypeCode}</span>
      ),
    },
    { field: 'source', header: 'Source' },
    {
      field: 'effectiveDate',
      header: 'Effective',
      body: (line: PayRunPopulationTransaction) => formatDate(line.effectiveDate),
    },
    {
      field: 'quantity',
      header: 'Units',
      body: (line: PayRunPopulationTransaction) =>
        typeof line.quantity === 'number' ? number.format(line.quantity) : '—',
    },
    {
      field: 'rate',
      header: 'Rate',
      body: (line: PayRunPopulationTransaction) =>
        typeof line.rate === 'number' ? currency.format(line.rate) : '—',
    },
    {
      field: 'amount',
      header: 'Amount',
      body: (line: PayRunPopulationTransaction) => currency.format(line.amount),
    },
  ];

  return (
    <div
      className="pay-run-employee-detail mgo-stack mgo-gap-m"
      data-testid={`pay-run-employee-detail-${employee.employmentRecordId}`}
    >
      {status.issues.map((issue) => (
        <Callout key={issue.id} tone={TONE[status.tone]} testId={`pay-run-employee-issue-${issue.id}`}
          title={(
            <span className="mgo-cluster mgo-gap-xs mgo-align-center">
              <span>{`${employee.employeeNumber}: ${employee.displayName}`}</span>
              <span className="mgo-weight-regular">{`— ${issue.message}`}</span>
            </span>
          )}
        />
      ))}

      {employee.transactions.length > 0
        ? (
          <div className="mgo-stack mgo-gap-xs">
            <span className="mgo-cluster mgo-gap-xs mgo-align-center">
              <span className="mgo-text-xs mgo-weight-strong mgo-color-muted">TRANSACTIONS</span>
              <NotImplementedHint
                what="Description, Type and Cost, and editing a line here"
                because="The population API returns a transaction's code, source, date, units, rate and amount only."
                testId={`pay-run-employee-transactions-hint-${employee.employmentRecordId}`}
              />
            </span>
            <Table<PayRunPopulationTransaction>
              value={employee.transactions}
              columns={columns}
              className="pay-run-employee-detail__table"
              testId={`pay-run-employee-transactions-${employee.employmentRecordId}`}
              emptyMessage="No transactions."
            />
          </div>
        )
        : (
          <span className="mgo-text-s mgo-color-muted">
            No transactions recorded against this employee for this Pay Run.
          </span>
        )}
    </div>
  );
};
